import React, { useState } from 'react';
import styled from 'styled-components';
import { Mongo } from 'meteor/mongo';
import { ModelListPage, ModelListPageStyled } from '/imports/ui/ModelListPage';
import { modelClass, SolarBill } from '/imports/api/solarBills';
import { clientDownloadUrl, formatCurrency, formatDate, formatDateTime, formatNumber, queryStringToObject } from '/imports/utils';
import { strings } from '/imports/strings';
import { Icon } from '/imports/ui/Icon';
import { PageHeading } from '/imports/ui/PageHeading';
import { useFindOne, useUser } from '/imports/hooks';
import { CalpineSendBillsModal } from '/imports/ui/CalpineSendBillsModal';
import { toastOnError } from '/imports/ui/utils';
import { DateInput } from '/imports/ui/DateInput';
import { getCalpineXlsxUrl, listCalpineBills } from '/imports/api/calpineBilling';
import { Accounts, Roles } from '/imports/api/accounts';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';

/** Formats a date-only string as a localized date, anchored to noon UTC. */
const formattedDate = (date: string) => formatDate(new Date(`${date}T12:00:00.000Z`));

const CalpineListPageStyled = styled(ModelListPageStyled)`
  table {
    font-size: 0.85rem;
    .row-select, .row-actions {
      display: none;
    }
  }
  td {
    &:nth-child(3) {
      max-width: 11rem;
    }
  }
  .header-buttons {
    display: flex;
    gap: 0.5rem;
  }
  .extra-filters {
    display: flex;
    gap: 1rem;
  }
  .filter {
    display: none;
  }
`;

/** Lists Calpine solar bills with XLSX download, date filtering, and batch send. */
export const CalpineListPage = () => {
  const queryParams = queryStringToObject();
  const batch = queryParams.batch;
  const user = useUser();
  const userId = user?._id;
  const orgId = user?.orgId;
  const account = useFindOne(() => Accounts.find({ orgId, userId }), [`${orgId}_${userId}`]);
  if (!user?.isSuperUser && !account?.roles?.includes(Roles.calpine)) return <NotPermittedPage />;
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showSendModal, setShowSendModal] = useState(false);
  const [query, setQuery] = useState<Mongo.Query<SolarBill>>(batch ? { batch: parseInt(batch) } : {});
  /**
   * Fetches a signed URL for a bill's XLSX and triggers a client-side download.
   * @param doc - The solar bill whose XLSX should be downloaded.
   */
  const downloadXlsx = toastOnError(async (doc: SolarBill) => {
    const url = await getCalpineXlsxUrl.call({ xlsxPath: doc.extra.xlsxPath });
    clientDownloadUrl(url);
  });
  return <ModelListPage
    modelClass={modelClass}
    listMethod={listCalpineBills}
    styled={CalpineListPageStyled}
    onSelectedIds={(selectedIds) => setSelectedIds(selectedIds)}
    header={(
      <PageHeading
        left={<h1>Calpine</h1>}
      />
    )}
    columns={[
      { field: 'xlsx', label: 'XLSX', render: (doc) => doc.extra?.xlsxPath ? <a href="#" onClick={async () => downloadXlsx(doc)}><Icon icon="docs" /></a> : '' },
      { field: 'billingPeriod', label: `${strings.Start()} → ${strings.End()}`, render: (doc) => <span>{formattedDate(doc.startDate).substring(0, 5)}→{formattedDate(doc.endDate)}</span> },
      { field: 'production', render: (doc) => <span>{formatNumber(doc.production)} kWh</span> },
      { field: 'solarCharges', label: strings.Amount(), render: (doc) => formatCurrency((doc.ppaCharges || 0) + (doc.fixedCharges || 0)), width: '3rem' },
      { field: 'createdAt', render: (doc) => <span>{doc.createdAt && formatDateTime(doc.createdAt)}</span> },
    ]}
    extraFilters={(
      <div className="extra-filters">
        <DateInput
          value={query.issuedDate as string}
          onChange={((issuedDate) => setQuery({ ...query, issuedDate }))}
          onClear={() => setQuery({ ...query, issuedDate: undefined })}
        />
      </div>
    )}
    defaultSort={{ createdAt: -1 }}
    query={query}
    footer={
      showSendModal ?
        <CalpineSendBillsModal billIds={selectedIds} onClose={() => setShowSendModal(false)} />
        :
        null
    }
  />;
};
